import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  CardContent,
  Container,
  Paper,
  Chip,
  LinearProgress,
} from "@mui/material";
import {
  LocalShipping as LocalShippingIcon,
  Payment as PaymentIcon,
  Folder as FolderIcon,
  Receipt as ReceiptIcon,
  PeopleAlt as PeopleAltIcon,
  CheckCircle as CheckCircleIcon,
  LocalShippingOutlined as OutForDeliveryIcon,
  Warehouse as ArrivedIcon,
  FlightTakeoff as ShippedIcon,
  Schedule as ShippingSoonIcon,
  RequestQuote as BilledIcon,
  AccountBalanceWallet as OutstandingIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { getDashboard } from "../../main/API/dashboard";
import { useSiteSettings } from "../../main/context/SiteSettingsContext";
import { getSiteDisplayName } from "../../main/utils/siteDisplayName";
import { OmrAmount } from "../../main/components/OmrAmount";
import { PERMISSIONS } from "../../main/context/AuthContext";
import { usePermissions } from "../../main/hooks/usePermissions";

export default function DashboardScreen() {
  const { t, i18n } = useTranslation("ui", { keyPrefix: "dashboard" });
  const { settings } = useSiteSettings();
  const { hasPermission } = usePermissions();
  const canViewDashboard = hasPermission(PERMISSIONS.DASHBOARD_VIEW_ANALYTICS);

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = async () => {
    if (!canViewDashboard) {
      setData(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await getDashboard();
      setData(response.data);
    } catch (err) {
      console.error("Error fetching dashboard data:", err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewDashboard]);

  const timeBasedData = useMemo(() => {
    if (!data?.time_based_analytics) return [];
    const tba = data.time_based_analytics;
    // Prefer new KPI names; fall back to legacy aliases (revenue / unpaid).
    const billedTba = tba.billed || {};
    const collectedTba = tba.collected || tba.revenue || {};
    const outstandingTba = tba.outstanding || tba.unpaid || {};
    const amountWithSymbol = (n) => (
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
        <OmrAmount value={n} />
      </Box>
    );
    return [
      { category: t("categories.shipments"), period: t("periods.weekly"), count: tba.shipments?.weekly, icon: <LocalShippingIcon />, color: "#4F46E5", bgColor: "#EEF2FF" },
      { category: t("categories.shipments"), period: t("periods.monthly"), count: tba.shipments?.monthly, icon: <LocalShippingIcon />, color: "#4F46E5", bgColor: "#EEF2FF" },
      { category: t("categories.shipments"), period: t("periods.yearly"), count: tba.shipments?.yearly, icon: <LocalShippingIcon />, color: "#4F46E5", bgColor: "#EEF2FF" },
      { category: t("categories.invoices"), period: t("periods.weekly"), count: tba.invoices?.weekly, icon: <ReceiptIcon />, color: "#F59E0B", bgColor: "#FFFBEB" },
      { category: t("categories.invoices"), period: t("periods.monthly"), count: tba.invoices?.monthly, icon: <ReceiptIcon />, color: "#F59E0B", bgColor: "#FFFBEB" },
      { category: t("categories.invoices"), period: t("periods.yearly"), count: tba.invoices?.yearly, icon: <ReceiptIcon />, color: "#F59E0B", bgColor: "#FFFBEB" },
      { category: t("categories.billed"), period: t("periods.weekly"), count: amountWithSymbol(billedTba.weekly), icon: <BilledIcon />, color: "#0EA5E9", bgColor: "#F0F9FF" },
      { category: t("categories.billed"), period: t("periods.monthly"), count: amountWithSymbol(billedTba.monthly), icon: <BilledIcon />, color: "#0EA5E9", bgColor: "#F0F9FF" },
      { category: t("categories.billed"), period: t("periods.yearly"), count: amountWithSymbol(billedTba.yearly), icon: <BilledIcon />, color: "#0EA5E9", bgColor: "#F0F9FF" },
      { category: t("categories.collected"), period: t("periods.weekly"), count: amountWithSymbol(collectedTba.weekly), icon: <PaymentIcon />, color: "#10B981", bgColor: "#ECFDF5" },
      { category: t("categories.collected"), period: t("periods.monthly"), count: amountWithSymbol(collectedTba.monthly), icon: <PaymentIcon />, color: "#10B981", bgColor: "#ECFDF5" },
      { category: t("categories.collected"), period: t("periods.yearly"), count: amountWithSymbol(collectedTba.yearly), icon: <PaymentIcon />, color: "#10B981", bgColor: "#ECFDF5" },
      { category: t("categories.outstanding"), period: t("periods.weekly"), count: amountWithSymbol(outstandingTba.weekly), icon: <OutstandingIcon />, color: "#EF4444", bgColor: "#FEF2F2" },
      { category: t("categories.outstanding"), period: t("periods.monthly"), count: amountWithSymbol(outstandingTba.monthly), icon: <OutstandingIcon />, color: "#EF4444", bgColor: "#FEF2F2" },
      { category: t("categories.outstanding"), period: t("periods.yearly"), count: amountWithSymbol(outstandingTba.yearly), icon: <OutstandingIcon />, color: "#EF4444", bgColor: "#FEF2F2" },
      { category: t("categories.users"), period: t("periods.weekly"), count: tba.users?.weekly, icon: <PeopleAltIcon />, color: "#8B5CF6", bgColor: "#F3F4F6" },
      { category: t("categories.users"), period: t("periods.monthly"), count: tba.users?.monthly, icon: <PeopleAltIcon />, color: "#8B5CF6", bgColor: "#F3F4F6" },
      { category: t("categories.users"), period: t("periods.yearly"), count: tba.users?.yearly, icon: <PeopleAltIcon />, color: "#8B5CF6", bgColor: "#F3F4F6" },
    ];
  }, [data, t]);

  if (isLoading || !data) {
    return (
      <Box
        component="main"
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <LinearProgress sx={{ width: 200, mb: 2, borderRadius: 1 }} />
          <Typography variant="h6" color="text.secondary">{t("loading")}</Typography>
        </Box>
      </Box>
    );
  }

  const {
    total_shipments,
    total_users,
    total_invoices,
    total_services,
    shipments_by_status,
  } = data;

  // Lifetime KPIs: billed + outstanding only (no lifetime collected).
  const financialSummary = data.financial_summary || {};
  const billed = financialSummary.billed ?? data.total_billed;
  const outstanding =
    financialSummary.outstanding ?? data.total_outstanding ?? data.total_unpaid;

  const getStatusConfig = (status) => {
    const statusConfigs = {
      delivered: {
        icon: <CheckCircleIcon />,
        color: "#10B981",
        bgColor: "#ECFDF5",
        label: t("status.delivered"),
      },
      "out for delivery": {
        icon: <OutForDeliveryIcon />,
        color: "#F59E0B",
        bgColor: "#FFFBEB",
        label: t("status.outForDelivery"),
      },
      arrived: {
        icon: <ArrivedIcon />,
        color: "#3B82F6",
        bgColor: "#EFF6FF",
        label: t("status.arrived"),
      },
      shipped: {
        icon: <ShippedIcon />,
        color: "#8B5CF6",
        bgColor: "#F3F4F6",
        label: t("status.shipped"),
      },
      "shipping soon": {
        icon: <ShippingSoonIcon />,
        color: "#6B7280",
        bgColor: "#F9FAFB",
        label: t("status.shippingSoon"),
      },
    };

    return statusConfigs[status.toLowerCase()] || {
      icon: <LocalShippingIcon />,
      color: "#6B7280",
      bgColor: "#F9FAFB",
      label: status,
    };
  };

  const statusOrder = ["shipping soon", "shipped", "arrived", "out for delivery", "delivered"];

  const shipmentStatusCards = statusOrder
    .map((status) => {
      const item = shipments_by_status.find((s) => s.status.toLowerCase() === status);
      if (!item) return null;

      return {
        id: status,
        status: item.status,
        count: item.count,
        ...getStatusConfig(item.status),
      };
    })
    .filter(Boolean);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#FAFAFA" }}>
      <Box component="main" sx={{ overflow: "auto", pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
        <Container maxWidth="xl">
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#1F2937", mb: 1 }}>
              {t("dashboardTitle", { siteName: getSiteDisplayName(settings, i18n.language) })}
            </Typography>
            <Typography variant="body1" color="#6B7280" sx={{ fontSize: "1.1rem" }}>
              {t("welcomeSubtitle")}
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              mb: 4,
              borderRadius: 3,
              border: "1px solid #E5E7EB",
              background: "linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)",
            }}
          >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: "#1F2937", mb: 1 }}>
                    {t("shipmentsByStatus")}
                  </Typography>
                  <Typography variant="body2" color="#6B7280">
                    {t("shipmentsByStatusDesc")}
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  {shipmentStatusCards.map((item) => (
                    <Grid item xs={12} sm={6} md={2.4} key={item.id}>
                      <Paper
                        elevation={0}
                        sx={{
                          height: "100%",
                          borderRadius: 2,
                          border: "1px solid #F3F4F6",
                          background: "#FFFFFF",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 8px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -5px rgba(0, 0, 0, 0.04)",
                            border: "1px solid #E5E7EB",
                          },
                        }}
                      >
                        <CardContent sx={{ p: 2.5 }}>
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
                            <Box
                              sx={{
                                p: 1.5,
                                borderRadius: 2,
                                backgroundColor: item.bgColor,
                                mb: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {React.cloneElement(item.icon, { sx: { color: item.color, fontSize: 24 } })}
                            </Box>
                            <Typography variant="h4" component="div" sx={{ fontWeight: 700, color: item.color, mb: 1 }}>
                              {item.count.toLocaleString()}
                            </Typography>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#1F2937", mb: 0.5 }}>
                              {item.label}
                            </Typography>
                            <Typography variant="body2" color="#6B7280">
                              {t("shipmentsWord")}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Paper>

          <Paper
            elevation={0}
            sx={{
              mb: 4,
              borderRadius: 3,
              border: "1px solid #E5E7EB",
              background: "linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#1F2937", mb: 1 }}>
                  {t("financialSummary")}
                </Typography>
                <Typography variant="body2" color="#6B7280">
                  {t("financialSummaryDesc")}
                </Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      height: "100%",
                      borderRadius: 2,
                      border: "1px solid #F3F4F6",
                      background: "#FFFFFF",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -5px rgba(0, 0, 0, 0.04)",
                        border: "1px solid #E5E7EB",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            backgroundColor: "#F0F9FF",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <BilledIcon sx={{ color: "#0EA5E9", fontSize: 24 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2" color="#6B7280" sx={{ fontWeight: 600 }}>
                            {t("totals.billed")}
                          </Typography>
                          <Typography variant="caption" color="#9CA3AF">
                            {t("totals.billedDesc")}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="h3" component="div" sx={{ fontWeight: 700, color: "#1F2937", display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                        <OmrAmount value={billed} />
                      </Typography>
                    </CardContent>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      height: "100%",
                      borderRadius: 2,
                      border: "1px solid #F3F4F6",
                      background: "#FFFFFF",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -5px rgba(0, 0, 0, 0.04)",
                        border: "1px solid #E5E7EB",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            backgroundColor: "#FEF2F2",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <OutstandingIcon sx={{ color: "#EF4444", fontSize: 24 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2" color="#6B7280" sx={{ fontWeight: 600 }}>
                            {t("totals.outstanding")}
                          </Typography>
                          <Typography variant="caption" color="#9CA3AF">
                            {t("totals.outstandingDesc")}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="h3" component="div" sx={{ fontWeight: 700, color: "#1F2937", display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                        <OmrAmount value={outstanding} />
                      </Typography>
                    </CardContent>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Paper>

          <Grid container spacing={3} sx={{ mb: 5 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    border: "1px solid #E5E7EB",
                    background: "linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                      border: "1px solid #D1D5DB",
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: "#EEF2FF",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <LocalShippingIcon sx={{ color: "#4F46E5", fontSize: 24 }} />
                      </Box>
                      <Typography variant="subtitle2" color="#6B7280" sx={{ fontWeight: 500 }}>
                        {t("totals.totalShipments")}
                      </Typography>
                    </Box>
                    <Typography variant="h3" component="div" sx={{ fontWeight: 700, color: "#1F2937" }}>
                      {total_shipments}
                    </Typography>
                  </CardContent>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    border: "1px solid #E5E7EB",
                    background: "linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                      border: "1px solid #D1D5DB",
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: "#F3F4F6",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <PeopleAltIcon sx={{ color: "#8B5CF6", fontSize: 24 }} />
                      </Box>
                      <Typography variant="subtitle2" color="#6B7280" sx={{ fontWeight: 500 }}>
                        {t("totals.totalUsers")}
                      </Typography>
                    </Box>
                    <Typography variant="h3" component="div" sx={{ fontWeight: 700, color: "#1F2937" }}>
                      {total_users}
                    </Typography>
                  </CardContent>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    border: "1px solid #E5E7EB",
                    background: "linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                      border: "1px solid #D1D5DB",
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: "#FFFBEB",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <ReceiptIcon sx={{ color: "#F59E0B", fontSize: 24 }} />
                      </Box>
                      <Typography variant="subtitle2" color="#6B7280" sx={{ fontWeight: 500 }}>
                        {t("totals.totalInvoices")}
                      </Typography>
                    </Box>
                    <Typography variant="h3" component="div" sx={{ fontWeight: 700, color: "#1F2937" }}>
                      {total_invoices}
                    </Typography>
                  </CardContent>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    border: "1px solid #E5E7EB",
                    background: "linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                      border: "1px solid #D1D5DB",
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: "#ECFDF5",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <FolderIcon sx={{ color: "#10B981", fontSize: 24 }} />
                      </Box>
                      <Typography variant="subtitle2" color="#6B7280" sx={{ fontWeight: 500 }}>
                        {t("totals.totalServices")}
                      </Typography>
                    </Box>
                    <Typography variant="h3" component="div" sx={{ fontWeight: 700, color: "#1F2937" }}>
                      {total_services}
                    </Typography>
                  </CardContent>
                </Paper>
              </Grid>
          </Grid>

          <Grid container spacing={4}>
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    border: "1px solid #E5E7EB",
                    background: "linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)",
                  }}
                >
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "#1F2937", mb: 1 }}>
                      {t("timeAnalytics")}
                    </Typography>
                    <Typography variant="body2" color="#6B7280">
                      {t("timeAnalyticsDesc")}
                    </Typography>
                  </Box>
                  <Grid container spacing={3}>
                    {timeBasedData.map((item, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Paper
                          elevation={0}
                          sx={{
                            height: "100%",
                            borderRadius: 2,
                            border: "1px solid #F3F4F6",
                            background: "#FFFFFF",
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 8px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -5px rgba(0, 0, 0, 0.04)",
                              border: "1px solid #E5E7EB",
                            },
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
                              <Box
                                sx={{
                                  p: 1,
                                  borderRadius: 1.5,
                                  backgroundColor: item.bgColor,
                                  flexShrink: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {React.cloneElement(item.icon, { sx: { color: item.color, fontSize: 20 } })}
                              </Box>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography
                                  variant="subtitle2"
                                  color="#1F2937"
                                  sx={{ fontWeight: 600, textAlign: "start" }}
                                >
                                  {item.category}
                                </Typography>
                                <Chip
                                  label={item.period}
                                  size="small"
                                  sx={{
                                    backgroundColor: item.bgColor,
                                    color: item.color,
                                    fontWeight: 500,
                                    fontSize: "0.75rem",
                                    height: 20,
                                    maxWidth: "100%",
                                    alignSelf: "flex-start",
                                    "& .MuiChip-label": {
                                      textAlign: "start",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    },
                                  }}
                                />
                              </Box>
                            </Box>
                            <Typography variant="h4" component="div" sx={{ fontWeight: 700, color: "#1F2937" }}>
                              {item.count}
                            </Typography>
                          </CardContent>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}
